import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BpcFooter from '@shared/layouts/components/footer/footer.vue';
import BpcSidebar from '@shared/layouts/components/sidebar/sidebar.vue';
import BpcHeader from '@shared/layouts/components/header/header.vue';
import BpcPublicHeader from '@shared/layouts/components/public-header/public-header.vue';
import BpcPublicFooter from '@shared/layouts/components/public-footer/public-footer.vue';
import { Widget } from '@cms/interfaces/widget';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { Observable } from 'rxjs';
import WidgetList from '@cms/components/widgets/widget-list/widget-list.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import { TenantPublicTheme } from '@shared/interfaces/theme';
import { VueReCaptcha } from 'vue-recaptcha-v3';

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY_V3,
  loaderOptions: { autoHideBadge: true },
});
library.add(faCog);

@Component({
  name: 'bpc-public-layout',
  components: {
    BpcHeader,
    BpcSidebar,
    BpcFooter,
    BpcPublicHeader,
    BpcPublicFooter,
    'bpc-widget-list': WidgetList,
    VuePerfectScrollbar,
    'font-awesome-icon': FontAwesomeIcon,
  },
})
export default class PublicLayout extends Vue {
  public isConfigOpen: boolean = false;

  // public showSidebar = false;
  public showFooter = false;
  public embed = false;
  public noPadding = false;

  public loading: boolean = false;
  public widgetsSubscription = null;
  public widgets: Widget[] = [];

  public loadedStyles: boolean = false;
  public isRecaptchaOk: boolean = false;
  /* Lifecycle hooks */
  // created () { }
  mounted() {
    this.recaptcha();
    const query = this.$route.query;
    this.$store.commit('shoppingCart/addPublicLayoutQueryParams', query);
    this.getWidgets();

    if ('showFooter' in query && query.showFooter === 'true') {
      this.showFooter = true;
    }

    if ('embed' in query && query.embed === 'true') {
      this.embed = true;
    }

    if ('noPadding' in query && query.noPadding === 'true') {
      this.noPadding = true;
    }
  }
  // updated () { }
  // destroyed () { }

  public toggleConfig(className) {
    const el = document.body;
    this.isConfigOpen = !this.isConfigOpen;

    if (this.isConfigOpen) {
      el.classList.add(className);
    } else {
      el.classList.remove(className);
    }
  }

  beforeMount() {
    import('@styles/' + this.tenantPublicTheme + '.scss');
    if (this.$store.getters['centerSettings/centerSettings']) {
      this.appendStyles();
    }
  }

  get isActivatedRecaptchaV3(): boolean {
    return process.env.VUE_APP_RECAPTCHA_V3_ACTIVATED === 'true';
  }

  async recaptcha() {
    if (this.isActivatedRecaptchaV3) {
      this.isRecaptchaOk = false;
      // Wait until recaptcha has been loaded.
      await this.$recaptchaLoaded();
      // Execute reCAPTCHA
      const token = await this.$recaptchaInstance.execute();
      // Check user score with the received token.
      const recaptchaPath = this.$bpcApiRouteHelper.getRecaptchaV3Path();
      const res = await this.$bpcApi.post(recaptchaPath, { token }).toPromise();
      // If the score is under a certain amount, redirect to other pages. This will only apply to bots
      if (res?.data?.score > 0.5) {
        this.isRecaptchaOk = true;
      }
    } else {
      this.isRecaptchaOk = true;
    }
  }

  get tenantAlias(): string {
    return this.$clientRouter.getTenantAlias();
  }

  get logo() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].logo_url
    ) {
      let logo = this.$store.getters['centerSettings/centerSettings'].logo_url;
      if (typeof logo === 'string') {
        logo = JSON.parse(logo);
      }
      return logo.url;
    }
    return null;
  }

  get name() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].name;
    }
    return null;
  }
  get header_image_url() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].header_image_url
    ) {
      let headerImageUrl = this.$store.getters['centerSettings/centerSettings'].header_image_url;
      if (typeof headerImageUrl === 'string') {
        headerImageUrl = JSON.parse(headerImageUrl);
      }
      return headerImageUrl.url;
    }
    return null;
  }
  get publicFooter() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].public_footer;
    }
    return null;
  }
  get publicCss() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].public_css;
    }
    return null;
  }
  get centerSettings() {
    return this.$store.getters['centerSettings/centerSettings'];
  }
  get tenantPublicThemeObject(): TenantPublicTheme[] {
    if (process.env.VUE_APP_PUBLIC_THEME) {
      const publicThemeArray = process.env.VUE_APP_PUBLIC_THEME.split(',');
      return publicThemeArray.map(tenantTheme => {
        const splittedTenantTheme = tenantTheme.split(':');
        const tenantPublicTheme = { tenant: splittedTenantTheme[0], theme: splittedTenantTheme[1] };
        tenantTheme = tenantPublicTheme;
        return tenantTheme;
      });
    }
    return null;
  }
  get tenantPublicTheme() {
    const publicThemeObject = this.tenantPublicThemeObject;
    if (publicThemeObject) {
      const selectedElement = publicThemeObject.find(tenantPublicTheme => {
        if (this.tenantAlias === tenantPublicTheme.tenant) {
          return true;
        }
      });
      return selectedElement?.theme || 'default';
    }
    return 'default';
  }

  get pageCanBeDisplayed(): boolean {
    return this.isRecaptchaOk; // NOTE: add more conditions if needed
  }

  @Watch('centerSettings')
  onCenterSettings() {
    this.appendStyles();
  }

  appendStyles() {
    const css = this.publicCss;
    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);
    /* if (style.styleSheet){
        // This is required for IE8 and below.
        style.styleSheet.cssText = css;
      } else {
        style.appendChild(document.createTextNode(css));
      } */
    style.appendChild(document.createTextNode(css));

    this.loadedStyles = true;
  }

  public getWidgets(): void {
    this.loading = true;

    const fetchEntityPath = `${this.$bpcApiRouteHelper.getEntityPath('widgets', 'raw')}`;

    // Parameters to filter the widgets in back
    const homepage = this.$route.meta?.homepage ? this.$route.meta?.homepage : false;
    const entity = this.$route.meta?.entity ? this.$route.meta?.entity : null;
    const alias = this.$route.params?.alias ? this.$route.params?.alias : null;

    const queryConfig = {
      params: {
        published: 1,
        orderBy: 'ordering',
        orderDirection: 'asc',
        homepage: homepage,
        entity: entity,
        alias: alias,
      },
    };

    const observable: Observable<BPCGenericApiResponse> = this.$bpcApi.get(
      fetchEntityPath,
      queryConfig,
      'public',
    );

    if (this.widgetsSubscription) {
      this.widgetsSubscription.unsubscribe();
    }

    this.widgetsSubscription = observable.subscribe(
      (res: BPCGenericApiResponse) => {
        this.widgets = res.data;
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      },
    );
  }

  public getWidgetsByArea(area: string): Widget[] {
    return this.widgets.filter(widget => widget.area === area);
  }

  public hasWidgetInArea(area: string): boolean {
    const isProfilePage = !!this.$route.meta['withSidebar'];
    return !isProfilePage && !!this.getWidgetsByArea(area).length;
  }

  public showSidebar(): boolean {
    return this.$authorizationService.hasPermissions([
      AuthorizationHelper.fixedPermissions.sections.main,
      AuthorizationHelper.fixedPermissions.sections.communication,
      AuthorizationHelper.fixedPermissions.sections.configuration,
    ]);
  }

  @Watch('$route', { immediate: true, deep: true })
  onUrlChange() {
    this.getWidgets();
  }
}
